import type { GameState } from '../state';
import { D, add, gt, type Decimal } from '../math/num';
import { getFrame } from '../data/frames';
import { getPartDef } from '../data/parts/partDefs';
import { RARITY_MULT, type PartInstance } from '../data/parts/partTypes';
import { coreBonuses } from './meta';
import { WEIGHT_PER_RANK } from '../data/smelter';

/** Weight governor (THE_MIND_AND_PARTS.md §4). */

export function findInstance(state: GameState, instanceId: string | null): PartInstance | undefined {
  if (!instanceId) return undefined;
  return state.inventory.parts.find((p) => p.instanceId === instanceId);
}

function affixSum(inst: PartInstance | undefined, stat: 'weightRed'): Decimal {
  if (!inst) return D(0);
  let t = D(0);
  for (const a of inst.affixes) if (a.stat === stat) t = add(t, a.value);
  return t;
}

export function partWeight(defId: string, inst: PartInstance | undefined): Decimal {
  return D(getPartDef(defId).weight).sub(affixSum(inst, 'weightRed')).max(0);
}

export function weightCapacity(state: GameState): Decimal {
  let cap = D(getFrame(state.frame.defId).weightBudget);
  for (const node of state.mind.nodes) {
    const def = getPartDef(node.defId);
    if (def.frame) {
      const inst = findInstance(state, node.instanceId);
      const rMult = RARITY_MULT[inst?.rarity ?? def.baseRarity];
      cap = add(cap, D(Math.round(def.frame.weightBonus * node.level * rMult)));
    }
  }
  cap = add(cap, D(coreBonuses(state).weightBonus));
  // Smelter Ballast Plating: permanent Alloy-bought capacity.
  cap = add(cap, D(WEIGHT_PER_RANK * (state.smelter.upgrades['su.ballast'] ?? 0)));
  return cap;
}

export function equippedWeight(state: GameState): Decimal {
  let w = D(0);
  for (const node of state.mind.nodes) {
    w = add(w, partWeight(node.defId, findInstance(state, node.instanceId)));
  }
  return w;
}

export function isOverWeight(state: GameState): boolean {
  return gt(equippedWeight(state), weightCapacity(state));
}
